import React, { useEffect, useState } from 'react';
import './AdminShopsManage.css';

function AdminShopsManage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [updatingRatings, setUpdatingRatings] = useState(false);
  const [newShopData, setNewShopData] = useState({
    name: '',
    website: '',
    logo: '',
    description: '',
    trustpilotReviewUrl: '',
    trustedShopsReviewUrl: ''
  });

  const handleColumnSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const res = await fetch('/api/stores');
      const data = await res.json();
      setShops(data);
    } catch (err) {
      console.error('Error fetching shops:', err);
      setMessage('Fehler beim Laden der Shops');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (shop) => {
    setEditingId(shop._id);
    setEditData({
      name: shop.name || '',
      website: shop.website || '',
      logo: shop.logo || '',
      trustpilotReviewUrl: shop.trustpilotReviewUrl || '',
      trustedShopsReviewUrl: shop.trustedShopsReviewUrl || ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveShop = async (shopId) => {
    try {
      // Get the actual JWT token from localStorage (set by login)
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('❌ Fehler: Bitte melde dich neu an');
        return;
      }

      // Save review links
      const reviewRes = await fetch(`/api/stores/${shopId}/admin/review-links`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trustpilotReviewUrl: editData.trustpilotReviewUrl,
          trustedShopsReviewUrl: editData.trustedShopsReviewUrl
        })
      });

      // Save shop details
      const shopRes = await fetch(`/api/stores/${shopId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editData.name,
          website: editData.website,
          logo: editData.logo
        })
      });

      if (!reviewRes.ok) {
        const errData = await reviewRes.json();
        throw new Error(errData.message || 'Fehler beim Speichern der Review-Links');
      }

      if (!shopRes.ok) {
        const errData = await shopRes.json();
        throw new Error(errData.message || 'Fehler beim Speichern der Shop-Daten');
      }

      const updatedShop = await shopRes.json();
      setMessage('✅ Shop gespeichert!');
      
      setShops(shops.map(s => 
        s._id === shopId 
          ? updatedShop
          : s
      ));
      
      setEditingId(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setMessage('❌ Fehler: ' + err.message);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const addNewShop = async () => {
    try {
      // Validierungen
      if (!newShopData.name.trim()) {
        setMessage('❌ Shop-Name ist erforderlich');
        return;
      }

      if (!newShopData.website.trim()) {
        setMessage('❌ Website ist erforderlich');
        return;
      }

      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('❌ Fehler: Bitte melde dich neu an');
        return;
      }

      const res = await fetch('/api/stores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newShopData.name.trim(),
          website: newShopData.website.trim(),
          logo: newShopData.logo.trim() || undefined,
          description: newShopData.description.trim() || undefined,
          trustpilotReviewUrl: newShopData.trustpilotReviewUrl.trim() || undefined,
          trustedShopsReviewUrl: newShopData.trustedShopsReviewUrl.trim() || undefined
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Fehler beim Erstellen des Shops');
      }

      const newShop = await res.json();
      setMessage('✅ Shop erfolgreich erstellt!');
      setShops([...shops, newShop]);
      setShowAddForm(false);
      setNewShopData({
        name: '',
        website: '',
        logo: '',
        description: '',
        trustpilotReviewUrl: '',
        trustedShopsReviewUrl: ''
      });
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Add shop error:', err);
      setMessage('❌ Fehler: ' + err.message);
    }
  };

  const handleNewShopInputChange = (e) => {
    const { name, value } = e.target;
    setNewShopData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const cancelAddForm = () => {
    setShowAddForm(false);
    setNewShopData({
      name: '',
      website: '',
      logo: '',
      description: '',
      trustpilotReviewUrl: '',
      trustedShopsReviewUrl: ''
    });
  };

  const deleteShop = async (shopId) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('❌ Fehler: Bitte melde dich neu an');
        return;
      }

      const res = await fetch(`/api/stores/${shopId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Fehler beim Löschen des Shops');
      }

      setMessage('✅ Shop erfolgreich gelöscht!');
      setShops(shops.filter(s => s._id !== shopId));
      setDeleteConfirm(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setMessage('❌ Fehler: ' + err.message);
    }
  };

  const updateAllRatings = async () => {
    try {
      setUpdatingRatings(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('❌ Fehler: Bitte melde dich neu an');
        setUpdatingRatings(false);
        return;
      }

      setMessage('🔄 Aktualisiere alle Bewertungen...');

      const res = await fetch('/api/stores/admin/sync-external-ratings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Deine Sitzung ist abgelaufen. Bitte melde dich neu an.');
        } else if (res.status === 403) {
          throw new Error('Keine Berechtigung für diese Aktion.');
        }
        const errData = await res.json();
        throw new Error(errData.message || 'Fehler beim Aktualisieren der Bewertungen');
      }

      const result = await res.json();
      
      // Refresh shop list to show updated ratings
      await fetchShops();
      
      setMessage(`✅ ${result.updatedCount} Shop${result.updatedCount !== 1 ? 's' : ''} aktualisiert (${result.errorCount} Fehler)`);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Update ratings error:', err);
      const errorMessage = err.message.includes('Sitzung ist abgelaufen') 
        ? err.message 
        : '❌ Fehler: ' + err.message;
      setMessage(errorMessage);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setUpdatingRatings(false);
    }
  };

  const getFilteredAndSortedShops = () => {
    let filtered = shops.filter(shop =>
      shop.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'website':
          aVal = (a.website || '').toLowerCase();
          bVal = (b.website || '').toLowerCase();
          break;
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  if (loading) return <div className="admin-shops-manage"><p>Lädt...</p></div>;

  return (
    <div className="admin-shops-manage">
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Bestätigungsdialog für Löschen */}
      {deleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-modal">
            <h3>⚠️ Shop wirklich löschen?</h3>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <p className="shop-name">{deleteConfirm.name}</p>
            <div className="confirmation-buttons">
              <button
                className="btn-confirm-delete"
                onClick={() => deleteShop(deleteConfirm._id)}
              >
                Ja, löschen
              </button>
              <button
                className="btn-cancel-delete"
                onClick={() => setDeleteConfirm(null)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suche */}
      <div className="shops-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Shop nach Name suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="shop-count">
          {getFilteredAndSortedShops().length} Shop{getFilteredAndSortedShops().length !== 1 ? 's' : ''}
        </div>

        <button
          className="btn-update-ratings"
          onClick={updateAllRatings}
          disabled={updatingRatings}
          title={updatingRatings ? 'Wird aktualisiert...' : 'Alle Bewertungen von Trustpilot aktualisieren'}
        >
          {updatingRatings ? '⏳ Wird aktualisiert...' : '🔄 Alle Bewertungen aktualisieren'}
        </button>

        <button
          className="btn-add-shop"
          onClick={() => setShowAddForm(!showAddForm)}
          title="Neuen Shop hinzufügen"
        >
          + Neuer Shop
        </button>
      </div>

      {/* Form zum Hinzufügen eines neuen Shops */}
      {showAddForm && (
        <div className="add-shop-form-wrapper">
          <div className="add-shop-form">
            <h3>➕ Neuen Shop erstellen</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Shop Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newShopData.name}
                  onChange={handleNewShopInputChange}
                  placeholder="z.B. Amazon"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Website *</label>
                <input
                  type="url"
                  name="website"
                  value={newShopData.website}
                  onChange={handleNewShopInputChange}
                  placeholder="https://example.com"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="url"
                  name="logo"
                  value={newShopData.logo}
                  onChange={handleNewShopInputChange}
                  placeholder="https://example.com/logo.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Beschreibung</label>
                <input
                  type="text"
                  name="description"
                  value={newShopData.description}
                  onChange={handleNewShopInputChange}
                  placeholder="kurze Beschreibung"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Trustpilot Review URL</label>
                <input
                  type="url"
                  name="trustpilotReviewUrl"
                  value={newShopData.trustpilotReviewUrl}
                  onChange={handleNewShopInputChange}
                  placeholder="https://trustpilot.com/..."
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Trusted Shops Review URL</label>
                <input
                  type="url"
                  name="trustedShopsReviewUrl"
                  value={newShopData.trustedShopsReviewUrl}
                  onChange={handleNewShopInputChange}
                  placeholder="https://www.trustedshops.com/..."
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-buttons">
              <button
                className="btn-confirm"
                onClick={addNewShop}
              >
                ✓ Shop erstellen
              </button>
              <button
                className="btn-cancel-form"
                onClick={cancelAddForm}
              >
                ✗ Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="shops-table-wrapper">
        <table className="shops-table">
          <thead>
            <tr>
              <th className="col-logo">Logo</th>
              <th
                className={`col-name sortable ${sortBy === 'name' ? `sorting-${sortOrder}` : ''}`}
                onClick={() => handleColumnSort('name')}
                title="Klicken zum Sortieren"
              >
                Shop Name
                {sortBy === 'name' && (
                  <span className="sort-indicator">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th
                className={`col-website sortable ${sortBy === 'website' ? `sorting-${sortOrder}` : ''}`}
                onClick={() => handleColumnSort('website')}
                title="Klicken zum Sortieren"
              >
                Website
                {sortBy === 'website' && (
                  <span className="sort-indicator">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th className="col-trustpilot">Trustpilot</th>
              <th className="col-trustedshops">Trusted Shops</th>
              <th className="col-actions">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredAndSortedShops().map(shop => (
              <tr key={shop._id} className={editingId === shop._id ? 'editing' : ''}>
                {editingId === shop._id ? (
                  <>
                    <td className="col-logo">
                      <input
                        type="url"
                        name="logo"
                        value={editData.logo}
                        onChange={handleInputChange}
                        placeholder="Logo URL"
                        className="input-small"
                      />
                    </td>
                    <td className="col-name">
                      <input
                        type="text"
                        name="name"
                        value={editData.name}
                        onChange={handleInputChange}
                        placeholder="Shop Name"
                        className="input-medium"
                      />
                    </td>
                    <td className="col-website">
                      <input
                        type="url"
                        name="website"
                        value={editData.website}
                        onChange={handleInputChange}
                        placeholder="https://..."
                        className="input-small"
                      />
                    </td>
                    <td className="col-trustpilot">
                      <input
                        type="url"
                        name="trustpilotReviewUrl"
                        value={editData.trustpilotReviewUrl}
                        onChange={handleInputChange}
                        placeholder="https://trustpilot.com/..."
                        className="input-small"
                      />
                    </td>
                    <td className="col-trustedshops">
                      <input
                        type="url"
                        name="trustedShopsReviewUrl"
                        value={editData.trustedShopsReviewUrl}
                        onChange={handleInputChange}
                        placeholder="https://www.trustedshops.com/..."
                        className="input-small"
                      />
                    </td>
                    <td className="col-actions">
                      <button
                        className="btn-save"
                        onClick={() => saveShop(shop._id)}
                        title="Speichern"
                      >
                        ✓
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={cancelEdit}
                        title="Abbrechen"
                      >
                        ✗
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="col-logo">
                      {shop.logo ? (
                        <img src={shop.logo} alt={shop.name} className="shop-logo-thumb" />
                      ) : (
                        <span className="no-value">—</span>
                      )}
                    </td>
                    <td className="col-name">{shop.name}</td>
                    <td className="col-website">
                      {shop.website ? (
                        <a href={shop.website} target="_blank" rel="noopener noreferrer" className="link-icon">
                          🔗
                        </a>
                      ) : (
                        <span className="no-value">—</span>
                      )}
                    </td>
                    <td className="col-trustpilot">
                      {shop.trustpilotReviewUrl ? (
                        <a href={shop.trustpilotReviewUrl} target="_blank" rel="noopener noreferrer" className="link-icon" title="Zur Trustpilot-Seite">
                          ⭐
                        </a>
                      ) : (
                        <span className="no-value">—</span>
                      )}
                    </td>
                    <td className="col-trustedshops">
                      {shop.trustedShopsReviewUrl ? (
                        <a href={shop.trustedShopsReviewUrl} target="_blank" rel="noopener noreferrer" className="link-icon" title="Zur Trusted Shops-Seite">
                          🏆
                        </a>
                      ) : (
                        <span className="no-value">—</span>
                      )}
                    </td>
                    <td className="col-actions">
                      <button
                        className="btn-edit"
                        onClick={() => startEdit(shop)}
                        title="Bearbeiten"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => setDeleteConfirm(shop)}
                        title="Löschen"
                      >
                        🗑️
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminShopsManage;
